import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import { FormSection } from "../../FormBits";
import { ActivityMedia } from "../ActivityLogTab";
import { formatWhen, isBlank, newestFirst, NoteEditor, NoteModal } from "./noteBits";

/*
External notes - shared with Contractors (external service centres), who see
them read-only. Ports Warranties/SingleWarranty/Activities/NoteItem.js (+ the
add flow in Activities.js), text notes only:
- notes are GET /ticket/{id}/'s `ticket_activities` with activity_type "note"
  (or null, which Activities also renders as a note)
- PUT ticket/{id}/note/ adds, PUT ticket/{id}/note/{noteId}/ edits; both are
  multipart `note`
- Edit only on your own text notes (subject carries the author's name), Logs
  when the note has a `change_log`; neither is offered to Contractors.
  Picture/video/PDF notes still render, but there's no upload here to edit them.
*/
const TicketNoteCard = function (props) {
  const { note, canEdit, canSeeLogs, onEdit, onLogs } = props;
  const media = note.list_media || [];
  return (
    <div className="tn-note">
      <div className="tn-note-head tn-note-head-split">
        <span className="tn-note-author">{note.subject || "Note"}</span>
        <div className="tn-note-actions tn-note-actions-inline">
          {canSeeLogs ? (
            <button type="button" className="tn-btn tn-btn-ghost tn-btn-sm" onClick={onLogs}>
              Logs
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" className="tn-btn tn-btn-ghost tn-btn-sm" onClick={onEdit}>
              Edit
            </button>
          ) : null}
        </div>
      </div>
      {media.length ? (
        <ActivityMedia media={media} />
      ) : (
        <div className="tn-note-body" dangerouslySetInnerHTML={{ __html: note.detail }} />
      )}
      <div className="tn-note-when tn-mono tn-note-foot">{formatWhen(note.modified || note.created)}</div>
    </div>
  );
};

const TicketNotesSection = function (props) {
  const { ticket, protectedFetch, userInfo, reloadTicket } = props;
  const ticketId = ticket && ticket.id;
  const user = (userInfo && userInfo.user) || {};
  const isContractor = !!(user.role && user.role.includes("Contractor"));

  const notes = newestFirst(
    ((ticket && ticket.ticket_activities) || []).filter(function (item) {
      return item.activity_type === "note" || item.activity_type === null;
    })
  );

  // null when closed; { note } when open (note null = add).
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState("");
  const [logsFor, setLogsFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const openEditor = function (note) {
    setDraft(note ? note.detail || "" : "");
    setError(null);
    setEditor({ note: note || null });
  };

  const closeEditor = function () {
    if (busy) return;
    setEditor(null);
  };

  const save = function () {
    if (busy || isBlank(draft)) return;
    const formData = new FormData();
    formData.append("note", draft);

    const url = editor.note
      ? apiEndpoints.ticket.note(ticketId) + editor.note.id + "/"
      : apiEndpoints.ticket.note(ticketId);

    setBusy(true);
    setError(null);
    protectedFetch(url, "PUT", formData, errorFunctions.checkResponseSent, true)
      .then(function (ok) {
        setBusy(false);
        if (!ok) {
          setError("Couldn't save the note. Please try again.");
          return;
        }
        setEditor(null);
        if (reloadTicket) reloadTicket();
      })
      .catch(function () {
        setBusy(false);
        setError("Couldn't save the note. Please try again.");
      });
  };

  return (
    <React.Fragment>
      <FormSection
        title="External Notes"
        note={
          isContractor ? null : (
            <button
              type="button"
              className="tn-btn tn-btn-primary tn-btn-sm"
              onClick={function () {
                openEditor(null);
              }}
              disabled={!ticketId}
            >
              Add Note
            </button>
          )
        }
      >
        {notes.length ? (
          <div className="tn-notes">
            {notes.map(function (note) {
              const isMine = !!(user.name && note.subject && note.subject.includes(user.name));
              const hasMedia = !!(note.list_media && note.list_media.length);
              return (
                <TicketNoteCard
                  key={note.id || note.created}
                  note={note}
                  canEdit={isMine && !isContractor && !hasMedia}
                  canSeeLogs={!isContractor && !!(note.change_log && note.change_log.length)}
                  onEdit={function () {
                    openEditor(note);
                  }}
                  onLogs={function () {
                    setLogsFor(note);
                  }}
                />
              );
            })}
          </div>
        ) : (
          <div className="tn-empty">No external notes yet.</div>
        )}
      </FormSection>

      {editor ? (
        <NoteModal
          title={editor.note ? "Edit External Note" : "Add External Note"}
          busy={busy}
          error={error}
          saveDisabled={isBlank(draft)}
          onSave={save}
          onClose={closeEditor}
        >
          <NoteEditor value={draft} onChange={setDraft} />
        </NoteModal>
      ) : null}

      {logsFor ? (
        <NoteModal
          title="Note logs"
          onClose={function () {
            setLogsFor(null);
          }}
        >
          <div className="tn-note-logs">
            <div className="tn-note-logs-label">Current note</div>
            <div className="tn-note tn-note-flat">
              <div className="tn-note-body" dangerouslySetInnerHTML={{ __html: logsFor.detail }} />
              <div className="tn-note-when tn-mono tn-note-foot">{formatWhen(logsFor.modified)}</div>
            </div>
            <div className="tn-note-logs-label">Previous notes</div>
            {(logsFor.change_log || [])
              .slice()
              .sort(function (a, b) {
                return b.id - a.id;
              })
              .map(function (log) {
                return (
                  <div className="tn-note tn-note-flat" key={log.id}>
                    <div className="tn-note-body" dangerouslySetInnerHTML={{ __html: log.old_value }} />
                    <div className="tn-note-when tn-mono tn-note-foot">{formatWhen(log.created)}</div>
                  </div>
                );
              })}
          </div>
        </NoteModal>
      ) : null}
    </React.Fragment>
  );
};

export default TicketNotesSection;
